import KTLabel, { KTLabelSize } from "general/components/OtherKeenComponents/KTLabel";
import CustomCard from "general/components/Others/CustomCard";
import VITableRowDropDown from "general/components/VITableRowDropDown";
import AppColors from "general/constants/AppColors";
import Utils from "general/utils/Utils";
import useRouter from "hooks/useRouter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BreadcrumbHelper from "general/components/CustomBreadcrumb/BreadcrumbHelper";
import UIHelper from "general/helpers/UIHelper";
import _ from "lodash";
import AppConstants from "general/constants/AppConstants";
import AgencyReconciliationCardView from "features/ReconciliationReport/SaleReconciliationReport/components/AgencyReconciliationCardView";
import FleetHelper from "features/Fleet/fleetHelper";
import EmptyView from "general/components/Others/EmptyView";
import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import AppConfigs from "general/constants/AppConfigs";
import KTPagination, { KTPaginationColor } from "general/components/OtherKeenComponents/KTPagination";
import KTTable from "general/components/OtherKeenComponents/KTTable";
import AppRoute from "general/constants/AppRoute";
import KTTooltip from "general/components/OtherKeenComponents/KTTooltip";
import tableStyle from "general/components/OtherKeenComponents/KTTable/tableStyle";
import { AppCustomDialogHelper } from "general/components/AppDialogCustom";
import FileUploadView from "general/components/quybka/Uploads/FileUploadView";

FleetReconciliationDetailView.propTypes = {};

function FleetReconciliationDetailView(props) {
    // MARK: ---- PROPERTIES ----
    const router = useRouter();
    const { t } = useTranslation();
    const { reconciliationId } = router.query;
    const agencyId = router?.query?.agencyId;
    const [reconciliationDetail, setReconciliationDetail] = useState(null);
    const [fleetDetail, setFleetDetail] = useState(null);
    const [ticketFilter, setTicketFilter] = useState({
        page: 0,
        limit: AppConfigs.defaultPageLimit,
    });
    const [ticketList, setTicketList] = useState([]);
    const [ticketTotal, setTicketTotal] = useState([]);
    const { user } = useSelector((state) => state.auth);
    const adminRole =
        user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
    const isOrganizationAgency = useMemo(() => {
        return reconciliationDetail?.agency?.type === 'ORGANIZATION';
    }, [reconciliationDetail]);

    const columns = useMemo(() => {
        return [
            {
                id: 'TICKET_CODE',
                name: UIHelper.buildTableColumn('Mã công việc'),
                selector: (row) => {
                    return (
                        <span
                            onClick={() => {
                                Utils.openInNewTab(`${AppRoute.ticketList}/${row.ticketId}`)
                            }}
                        >
                            {row?.ticketCode ?? (row.ticketId)}
                        </span>
                    )
                },
                style: tableStyle.columns.link,
                sortable: false,
                width: '150px',
            },
            {
                id: 'STATION_NAME',
                name: UIHelper.buildTableColumn('Trạm sạc'),
                minWidth: '240px',
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.chargeStation?.name ?? ''}>
                            <span
                                onClick={() => {
                                    Utils.openInNewTab(`${AppRoute.stationList}/${row?.chargeStation?.stationId}`)
                                }}
                            >{row?.chargeStation?.name ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.link,
                sortable: false,
            },
            {
                id: 'POINT_NAME',
                name: UIHelper.buildTableColumn('Trụ sạc lỗi'),
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.chargePoint?.pointCode ?? ''}>
                            <span
                                onClick={() => {
                                    Utils.openInNewTab(`${AppRoute.pointList}/${row?.chargePoint?.pointId}`)
                                }}
                            >{row?.chargePoint?.pointCode ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.link,
                sortable: false,
                width: '130px',
            },
            {
                id: 'TYPE',
                name: UIHelper.buildTableColumn('Loại công việc'),
                selector: (row) => _.find(FleetHelper.Data.ticketTypeOptions, (item) => item.value === row?.type)?.name,
                style: tableStyle.columns.normal,
                sortable: false,
                width: '130px',
            },
            {
                id: 'CREATED_AT',
                name: UIHelper.buildTableColumn('Thời gian gửi lỗi'),
                selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
            },
            {
                id: 'COMPLETED_DATE',
                name: UIHelper.buildTableColumn('Thời gian hoàn thành'),
                selector: (row) => Utils.formatDateTimeToString(row?.completedAt, 'DD/MM/YYYY - HH:mm'),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
            },
            {
                id: 'PAYMENT_STATUS',
                name: UIHelper.buildTableColumn('Trạng thái thanh toán'),
                selector: (row) => {
                    return (
                        <KTLabel
                            text={FleetHelper.Utils.getPaymentStatusName(row?.paymentStatus)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${FleetHelper.Utils.getPaymentStatusColor(row.paymentStatus)}`}
                        />
                    );
                },
                sortable: false,
                width: '170px',
            },
            {
                id: 'TOTAL_AMOUNT',
                name: UIHelper.buildTableColumn('Tổng chi phí', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.amount),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
                right: true,
            },
            {
                id: 'TAX',
                name: UIHelper.buildTableColumn('Thuế TNCN', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.amount * row?.tax),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
                right: true,
                omit: isOrganizationAgency,
            },
            {
                id: 'VAT',
                name: UIHelper.buildTableColumn('Thuế VAT', 'VNĐ'),
                selector: (row) => Utils.formatNumber(row?.amount * row?.vat),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
                right: true,
                omit: !isOrganizationAgency,
            },
            {
                id: 'RECEIVED_AMOUNT',
                name: UIHelper.buildTableColumn('Tổng nhận', 'VNĐ'),
                selector: (row) => {
                    const netAmount = isOrganizationAgency ? row?.amount * (1 + row?.vat) : row?.amount * (1 - row?.tax);
                    return Utils.formatNumber(netAmount)
                },
                style: tableStyle.columns.normal,
                sortable: false,
                width: '160px',
                right: true,
            },
            {
                id: 'INVOICE',
                name: UIHelper.buildTableColumn('Chứng từ thanh toán'),
                selector: (row) => {
                    return row?.paymentDocument?.length > 0 ? (
                        <span
                            className="hover-opacity-60 cursor-pointer"
                            onClick={() => {
                                handleViewFiles(row?.paymentDocument);
                            }}
                        >
                            {`Xem chi tiết (${row?.paymentDocument?.length})`}
                        </span>
                    ) : (
                        <></>
                    );
                },
                style: tableStyle.columns.link,
                sortable: false,
                width: '160px',
            },
        ];
    }, [isOrganizationAgency]);

    useEffect(() => {
        handleGetReconciliationDetail();
    }, [reconciliationId, agencyId]);

    useEffect(() => {
        handleGetTicketList();
    }, [reconciliationDetail, ticketFilter]);

    // MARK: ---- METHOD ----

    const handleGetReconciliationDetail = () => {
        if (reconciliationId) {
            FleetHelper.API.requestGetReconciliationDetail(reconciliationId).then(
                (reconciliationDetail) => {
                    setReconciliationDetail(reconciliationDetail);
                    setFleetDetail(reconciliationDetail?.agency);
                }
            );
        } else if (agencyId) {
            const paramGetReconciliationList = {
                agencyId,
            };
            FleetHelper.API.requestGetReconciliationList(paramGetReconciliationList).then(
                (reconciliationList) => {
                    setReconciliationDetail(_.maxBy(reconciliationList, 'reconciliationId'));
                }
            );
        }
    }

    const handleGetTicketList = () => {
        if (reconciliationDetail?.metadata?.doneTickets?.length) {
            const startOfMonth = moment(reconciliationDetail?.month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
            const endOfMonth = moment(reconciliationDetail?.month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
            const params = {
                ...ticketFilter,
                status: 'DONE',
                from: startOfMonth,
                to: endOfMonth
            }
            if (adminRole) {
                params.userId = reconciliationDetail?.metadata?.doneTickets?.[0]?.userId;
                FleetHelper.API.requestGetTicketList(params).then(
                    ({ tickets, total }) => {
                        setTicketList(tickets);
                        setTicketTotal(total);
                    }
                );
            } else {
                FleetHelper.API.requestGetMyTicket(params).then(
                    ({ tickets, total }) => {
                        setTicketList(tickets);
                        setTicketTotal(total);
                    }
                );
            }
        }
    }

    const handleViewFiles = (files) => {
        AppCustomDialogHelper.show(
            'Tệp đính kèm',
            <FileUploadView
                pathList={files}
                onCancel={() => {
                    AppCustomDialogHelper.hide();
                }}
            />,
            'md',
            false,
            true
        );
    };

    const FleetReconciliationInfo = () => {
        return (
            <div
                className="row m-0 position-relative p-3 bg-white rounded-lg"
                style={{
                    border: `1px solid ${AppColors.gray[300]}`,
                    boxShadow: '0px 7px 8px -3px #0000000D',
                }}
            >
                <div className="d-flex align-items-center w-100 p-0">
                    <div className="d-flex flex-column w-100">
                        <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
                            <div
                                className="font-weight-bolder"
                                style={{
                                    color: AppColors.gray[800],
                                    fontSize: Utils.returnFontSize(18),
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {fleetDetail?.name}{" "}
                                • {fleetDetail?.agencyCode ?? fleetDetail?.agencyId}
                            </div>

                            <span className="min-w-100px ml-3">
                                <KTLabel
                                    text={FleetHelper.Utils.getReconciliationPaymentStatusName(reconciliationDetail?.metadata?.totalTicket, reconciliationDetail?.metadata?.receivedTicket)}
                                    isInline={true}
                                    size={KTLabelSize.medium}
                                    additionalClassName={`label-light-${FleetHelper.Utils.getReconciliationPaymentStatusColor(
                                        reconciliationDetail?.metadata?.totalTicket, reconciliationDetail?.metadata?.receivedTicket
                                    )}`}
                                />
                            </span>
                        </div>
                        <div style={{ fontSize: Utils.returnFontSize(14), color: AppColors.gray[700] }}>
                            Đối soát doanh thu kỹ thuật viên tháng {Utils.formatDateTimeToString(reconciliationDetail?.month, 'MM/yyyy')}
                        </div>
                    </div>

                    <div className="d-flex gap-3">
                        <VITableRowDropDown
                            toggleElement={
                                <a
                                    href="#"
                                    className="btn btn-sm btn-clean btn-icon h-45px w-45px hover-opacity-60"
                                    style={{ backgroundColor: AppColors.gray[200] }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-ellipsis"
                                        style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                    />
                                </a>
                            }
                            menuItem={[
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-file-chart-column"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem file đối soát',
                                    click: () => {
                                        if (Utils.isValid(reconciliationDetail?.document)) {
                                            Utils.openInNewTab(Utils.getFullUrl(reconciliationDetail?.document));
                                        }
                                    },
                                    show: !!reconciliationDetail?.document,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-arrow-rotate-right"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Tạo lại đối soát',
                                    click: () => {
                                        FleetHelper.UI.showDialogCreateReconciliation(reconciliationDetail, handleGetReconciliationDetail)
                                    },
                                    show: adminRole,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-download"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Tải đối soát excel',
                                    click: () => Utils.openInCurrentTab(reconciliationDetail?.documentXLSX),
                                    show: !!reconciliationDetail?.documentXLSX,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-message-exclamation"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Gửi phản hồi',
                                    click: () => FleetHelper.UI.showDialogReconciliationFeedback(reconciliationDetail, adminRole, handleGetReconciliationDetail),
                                    show: !adminRole,
                                },
                                {
                                    iconElement: (
                                        <i
                                            className="far fa-message-exclamation"
                                            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                        ></i>
                                    ),
                                    text: 'Xem phản hồi',
                                    click: () => FleetHelper.UI.showDialogReconciliationFeedback(reconciliationDetail, adminRole, handleGetReconciliationDetail),
                                    show: adminRole && !!reconciliationDetail?.feedback,
                                },
                            ]}
                            singleBtnBgColor={AppColors.gray[200]}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const groupCommissionList = useMemo(() => {
        return [
            {
                name: 'Tổng chi phí sửa chữa/bảo trì',
                value: Math.floor(reconciliationDetail?.amount),
                unit: 'VNĐ',
                color: '#30BD6F',
                icon: 'fa-solid fa-badge-dollar',
                show: true,
            },
            {
                name: isOrganizationAgency ? 'Tổng thuế VAT' : 'Tổng thuế TNCN',
                value: Math.floor(isOrganizationAgency ? reconciliationDetail?.metadata?.vatAmount : reconciliationDetail?.metadata?.taxAmount),
                unit: 'VNĐ',
                color: '#FFAA24',
                icon: 'fa-solid fa-badge-percent',
                show: true,
            },
            {
                name: 'Tổng tiền KTV được nhận (sau thuế)',
                value: Math.floor(reconciliationDetail?.netAmount),
                unit: 'VNĐ',
                color: '#168EE9',
                icon: 'fa-solid fa-badge-dollar',
                show: true,
            }
        ];
    }, [reconciliationDetail, adminRole, isOrganizationAgency]);

    const FleetReconciliationRevenueInfo = () => {
        return (
            <div
                className="overflow-hidden position-relative mt-4"
                style={{
                    border: `1px solid ${AppColors.gray[300]}`,
                    borderRadius: '10px',
                }}
            >
                <div
                    style={{
                        backgroundColor: '#fff',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                    }}
                >
                    {/* barchart */}
                    <div className="w-100 d-flex flex-column gap-1 p-4"
                        style={{
                            borderBottom: `1px solid ${AppColors.gray[300]}`
                        }}
                    >
                        <div style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>
                            Doanh thu kỹ thuật viên được nhận
                        </div>
                    </div>
                    {/* body */}
                    <div className="w-100 d-flex p-4 gap-3">
                        {groupCommissionList?.filter(cardInfo => cardInfo?.show)
                            ?.map((cardInfo, index) => (
                                <div
                                    key={index}
                                    className="flex-grow-1 p-0"
                                >
                                    <AgencyReconciliationCardView
                                        name={cardInfo.name}
                                        value={cardInfo.value}
                                        unit={cardInfo.unit}
                                        color={cardInfo.color}
                                        icon={cardInfo.icon}
                                        descriptionValue={cardInfo.descriptionValue}
                                        additionalDes={cardInfo?.additionalDes}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            </div >
        )
    }

    return (
        <div>
            <div className="w-100">
                {BreadcrumbHelper.buildBreadcrumb([
                    {
                        title: 'Báo cáo đối soát kỹ thuật viên',
                        onPress: () => {
                            router.navigate(-1);
                        },
                    },
                    { title: 'Chi tiết báo cáo đối soát' },
                ])}
            </div>

            <div className="m-4 mt-16">
                <CustomCard
                    showHeader={false}
                    additionalClassName="rounded-lg overflow-hidden"
                    bgTransparent={true}
                    showBorder={false}
                    body={
                        <div className="d-flex flex-column">
                            <div
                                className={`col-12 p-0 h-100`}
                            >
                                <FleetReconciliationInfo />

                                <FleetReconciliationRevenueInfo />

                            </div>

                        </div>
                    }
                />

                {/* table */}
                <div className="mt-4">
                    {UIHelper.buildCard(
                        <div className="p-4">
                            <div style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                Công việc đã hoàn thành
                            </div>
                            <div>
                                <KTTable
                                    columns={columns}
                                    items={ticketList}
                                    selectableRows={false}
                                    striped={false}
                                    compactGrid={false}
                                    hasActionColumn={false}
                                    onClickedRow={(row) => { }}
                                    onSelectedRowsChanged={(rows) => { }}
                                    onDoubleClickedRow={(rows) => { }}
                                    noDataComponent={
                                        <EmptyView
                                            icon={imgEmptyNoData}
                                            title="Không có dữ liệu để hiển thị"
                                            text={<span>Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.</span>}
                                        />
                                    }
                                />
                                <div className="px-4 mt-2">
                                    <KTPagination
                                        currentPage={ticketFilter.page + 1}
                                        rowCount={ticketTotal != 0 ? ticketTotal : ticketList?.length}
                                        rowsPerPage={ticketFilter.limit}
                                        currentItemInPageCount={ticketList?.length}
                                        onChangePage={(newPage) => {
                                            const iNewPage = parseInt(newPage);
                                            setTicketFilter({
                                                ...ticketFilter,
                                                page: iNewPage - 1,
                                            })
                                        }}
                                        onChangeRowsPerPage={(newRowsPerPage) => {
                                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                                            setTicketFilter({
                                                ...ticketFilter,
                                                limit: iNewRowsPerPage,
                                                page: 0,
                                            })
                                        }}
                                        color={KTPaginationColor.success}
                                        appendText="công việc"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FleetReconciliationDetailView;
